import {useEffect, useState} from "react";
import {Link, NavLink, useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../services/use-auth";
import {useMetaTags} from "../../services/useMetaTags";
import AdvertiseBlock from "../../components/AdvertiseBlock";
import trackEvent from "../../services/trackEvent";
import trackPageView from "../../services/trackPageView";

import "../../assets/styles/events/conferenceBrowser.scss";

const PER_PAGE = 15;

function normalizeSortValue(value) {
    return value === "upcoming" ? "upcoming" : "newest";
}

function parsePositiveInt(value, fallback = 1) {
    const parsed = Number.parseInt(String(value || ""), 10);

    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }

    return parsed;
}

function normalizeFilterValue(value) {
    const normalized = String(value || "").trim();

    return normalized === "" ? "0" : normalized;
}

function parseQueryState(search) {
    const params = new URLSearchParams(search || "");

    return {
        search: params.get("search") || "",
        sort: normalizeSortValue(params.get("sort") || ""),
        type: normalizeFilterValue(params.get("type")),
        hasPrice: normalizeFilterValue(params.get("hasPrice")),
        speciality: normalizeFilterValue(params.get("speciality")),
        certificate: normalizeFilterValue(params.get("certificate")),
        city: normalizeFilterValue(params.get("city")),
        organizer: normalizeFilterValue(params.get("organizer")),
        dateFrom: params.get("dateFrom") || "",
        dateTo: params.get("dateTo") || "",
        page: parsePositiveInt(params.get("page"), 1),
    };
}

function buildRouteQuery(state) {
    const params = new URLSearchParams();

    if ((state.search || "").trim() !== "") {
        params.set("search", state.search.trim());
    }

    if (normalizeSortValue(state.sort) !== "newest") {
        params.set("sort", normalizeSortValue(state.sort));
    }

    if (normalizeFilterValue(state.type) !== "0") {
        params.set("type", normalizeFilterValue(state.type));
    }

    if (normalizeFilterValue(state.hasPrice) !== "0") {
        params.set("hasPrice", normalizeFilterValue(state.hasPrice));
    }

    if (normalizeFilterValue(state.speciality) !== "0") {
        params.set("speciality", normalizeFilterValue(state.speciality));
    }

    if (normalizeFilterValue(state.certificate) !== "0") {
        params.set("certificate", normalizeFilterValue(state.certificate));
    }

    if (normalizeFilterValue(state.city) !== "0") {
        params.set("city", normalizeFilterValue(state.city));
    }

    if (normalizeFilterValue(state.organizer) !== "0") {
        params.set("organizer", normalizeFilterValue(state.organizer));
    }

    if ((state.dateFrom || "").trim() !== "") {
        params.set("dateFrom", state.dateFrom);
    }

    if ((state.dateTo || "").trim() !== "") {
        params.set("dateTo", state.dateTo);
    }

    if ((state.page || 1) > 1) {
        params.set("page", String(state.page));
    }

    return params.toString();
}

function buildApiQuery(state) {
    const params = new URLSearchParams();

    params.set("page", String(state.page || 1));
    params.set("sort", normalizeSortValue(state.sort));

    if ((state.search || "").trim() !== "") {
        params.set("search", state.search.trim());
    }

    if (normalizeFilterValue(state.type) !== "0") {
        params.set("type", normalizeFilterValue(state.type));
    }

    if (normalizeFilterValue(state.hasPrice) !== "0") {
        params.set("hasPrice", normalizeFilterValue(state.hasPrice));
    }

    if (normalizeFilterValue(state.speciality) !== "0") {
        params.set("speciality", normalizeFilterValue(state.speciality));
    }

    if (normalizeFilterValue(state.certificate) !== "0") {
        params.set("certificate", normalizeFilterValue(state.certificate));
    }

    if (normalizeFilterValue(state.city) !== "0") {
        params.set("city", normalizeFilterValue(state.city));
    }

    if (normalizeFilterValue(state.organizer) !== "0") {
        params.set("organizer", normalizeFilterValue(state.organizer));
    }

    if ((state.dateFrom || "").trim() !== "") {
        params.set("dateFrom", state.dateFrom);
    }

    if ((state.dateTo || "").trim() !== "") {
        params.set("dateTo", state.dateTo);
    }

    return params.toString();
}

function getPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items = [1];
    const from = Math.max(2, currentPage - 1);
    const to = Math.min(totalPages - 1, currentPage + 1);

    if (from > 2) {
        items.push("ellipsis-left");
    }

    for (let page = from; page <= to; page += 1) {
        items.push(page);
    }

    if (to < totalPages - 1) {
        items.push("ellipsis-right");
    }

    items.push(totalPages);

    return items;
}

function findFilterLabel(options, value, t) {
    const normalizedValue = String(value || "");

    for (const option of Array.isArray(options) ? options : []) {
        if (String(option?.value || "") !== normalizedValue) {
            continue;
        }

        if (option?.labelKey) {
            return t(option.labelKey);
        }

        if (option?.label) {
            return String(option.label);
        }
    }

    return normalizedValue;
}

function normalizeUrl(value) {
    if (!value) {
        return "/img/fallback-min.png";
    }

    if (/^(https?:)?\/\//.test(value)) {
        return value;
    }

    return value.startsWith("/") ? value : `/${value}`;
}

function buildConferenceCardBackground(image) {
    const normalizedImage = normalizeUrl(image);

    return `linear-gradient(180deg, rgba(0,0,0,.18), rgba(0,0,0,.58)), url('${normalizedImage}')`;
}

function formatNumericValue(value) {
    const numeric = Number(value);

    if (!Number.isFinite(numeric)) {
        return null;
    }

    if (Number.isInteger(numeric)) {
        return String(numeric);
    }

    return String(numeric).replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");
}

function formatConferencePrice(prices, isFinished, t) {
    if (isFinished) {
        return {
            label: t("conferences.finished"),
            isFree: false,
        };
    }

    const items = Array.isArray(prices) ? prices : [];

    if (items.length === 0) {
        return {
            label: t("conferences.free"),
            isFree: true,
        };
    }

    const parts = items
        .map((price) => {
            const amount = formatNumericValue(price?.amount ?? price?.value ?? price?.price);
            const code = String(price?.currency?.code || price?.currencyCode || "").trim().toLowerCase();

            if (!amount || !code) {
                return null;
            }

            return `${amount} ${t(`app.currencies.${code}`, code.toUpperCase())}`;
        })
        .filter(Boolean);

    if (parts.length === 0) {
        return {
            label: t("conferences.free"),
            isFree: true,
        };
    }

    return {
        label: parts.join(" · "),
        isFree: false,
    };
}

function formatConferenceLocation(location) {
    if (!Array.isArray(location)) {
        return "";
    }

    return location
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .join(", ");
}

function formatConferenceDate(dateStart, dateEnd) {
    const normalizedStart = String(dateStart || "").trim();
    const normalizedEnd = String(dateEnd || "").trim();

    if (!normalizedStart) {
        return normalizedEnd;
    }

    if (!normalizedEnd || normalizedEnd.includes(normalizedStart)) {
        return normalizedEnd || normalizedStart;
    }

    return `${normalizedStart} - ${normalizedEnd}`;
}

function ConferenceCard({item, isPinned = false, t}) {
    const price = formatConferencePrice(item?.prices, item?.isFinished, t);
    const locationLabel = formatConferenceLocation(item?.location);
    const dateLabel = formatConferenceDate(item?.dateStart, item?.dateEnd);
    const hasCertificate = Number(item?.hasCertificate || 0) > 0;
    const sponsor = Array.isArray(item?.sponsors) ? item.sponsors[0] : null;
    const groupCount = Number(item?.groupItemsCount || 0);

    return (
        <article className="conf-card">
            <Link
                className="conf-card__media"
                to={`/conference/${item.id}`}
                style={{backgroundImage: buildConferenceCardBackground(item?.image)}}>
                {isPinned && (
                    <span className="conf-badge conf-badge--top">
                        {t("conferences.top")}
                    </span>
                )}

                <span className={`conf-badge conf-badge--price ${price.isFree ? "is-free" : ""}`}>
                    {price.label}
                </span>

                {sponsor?.image && (
                    <div className="conf-card__sponsor">
                        <img src={normalizeUrl(sponsor.image)} alt="" loading="lazy" />
                    </div>
                )}

                <div className="conf-card__overlay">
                    <div className="conf-card__kicker">
                        {item?.isGroup
                            ? t("main.updates.conferenceGroupCaption", "Conference group")
                            : t("conferences.title")}
                    </div>
                    <div className="conf-card__title">{item?.name}</div>
                </div>
            </Link>

            <div className="conf-card__body">
                <div className="conf-card__stats">
                    {!!locationLabel && (
                        <span className="conf-stat">
                            <span className="conf-stat__ic">⌖</span>
                            <span className="conf-stat__val">{locationLabel}</span>
                        </span>
                    )}
                    {!!dateLabel && (
                        <span className="conf-stat">
                            <span className="conf-stat__ic">◷</span>
                            <span className="conf-stat__val">{dateLabel}</span>
                        </span>
                    )}
                    {hasCertificate && (
                        <span className="conf-stat">
                            <span className="conf-stat__ic">▣</span>
                            <span className="conf-stat__val">{t("conferences.filters.certification")}</span>
                        </span>
                    )}
                </div>

                {item?.isGroup && groupCount > 0 && (
                    <div className="conf-card__group">
                        {t("main.updates.conferenceGroupCaption", "Conference group")} {groupCount}
                    </div>
                )}
            </div>
        </article>
    );
}

function ConferenceBrowser({favoriteOnly = false}) {
    const {t} = useTranslation();
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();
    const queryState = parseQueryState(location.search);
    const [draft, setDraft] = useState(queryState);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState({
        items: [],
        pinnedItems: [],
        pagination: {
            page: 1,
            perPage: PER_PAGE,
            totalItems: 0,
            totalPages: 0,
            hasMore: false,
        },
        filtersMeta: {
            types: [],
            prices: [],
            specialties: [],
            certificates: [],
            cities: [],
            organizers: [],
        },
    });

    const pageTitle = favoriteOnly ? t("conferences.favoriteTitle") : t("conferences.title");
    const pagePath = favoriteOnly ? "/conferences/favorite" : "/conferences";
    const endpoint = favoriteOnly ? "/api/v1/conferences/favorite/browser" : "/api/v1/conferences/browser";
    const preservedSearch = buildRouteQuery(queryState);
    const stateLabel = favoriteOnly ? t("conferences.tabs.favorite") : t("conferences.tabs.all");
    const hasActiveFilterChips = queryState.type !== "0"
        || queryState.hasPrice !== "0"
        || queryState.speciality !== "0"
        || queryState.certificate !== "0"
        || queryState.city !== "0"
        || queryState.organizer !== "0"
        || queryState.dateFrom !== ""
        || queryState.dateTo !== "";
    const hasSearchTerm = queryState.search.trim() !== "";
    const paginationItems = getPaginationItems(data.pagination.page || 1, data.pagination.totalPages || 0);
    const sortedPinnedItems = [...(Array.isArray(data.pinnedItems) ? data.pinnedItems : [])].sort((left, right) => {
        return Number(left?.position || 0) - Number(right?.position || 0);
    });
    const pinnedConferenceIds = sortedPinnedItems.map((entry) => Number(entry?.item?.id || 0)).filter(Boolean);
    const conferenceItems = (Array.isArray(data.items) ? data.items : []).filter((item) => {
        return !pinnedConferenceIds.includes(Number(item?.id || 0));
    });

    useMetaTags({
        title: pageTitle,
        description: t("app.description"),
        metas: [
            {name: "keywords", content: t("app.keywords")},
        ],
    });

    useEffect(() => {
        trackPageView(pageTitle);
    }, [pageTitle]);

    useEffect(() => {
        setDraft(queryState);
    }, [
        queryState.search,
        queryState.sort,
        queryState.type,
        queryState.hasPrice,
        queryState.speciality,
        queryState.certificate,
        queryState.city,
        queryState.organizer,
        queryState.dateFrom,
        queryState.dateTo,
        queryState.page,
    ]);

    useEffect(() => {
        const controller = new AbortController();

        setIsLoading(true);

        fetch(`${endpoint}?${buildApiQuery(queryState)}`, {
            signal: controller.signal,
        })
            .then(async (response) => {
                const payload = await response.json();

                if (!response.ok) {
                    if (payload?.redirectUrl) {
                        history.replace(payload.redirectUrl);
                    }

                    throw new Error("Conference browser request failed");
                }

                setData({
                    items: Array.isArray(payload?.items) ? payload.items : [],
                    pinnedItems: Array.isArray(payload?.pinnedItems) ? payload.pinnedItems : [],
                    pagination: payload?.pagination || {
                        page: 1,
                        perPage: PER_PAGE,
                        totalItems: 0,
                        totalPages: 0,
                        hasMore: false,
                    },
                    filtersMeta: payload?.filtersMeta || {
                        types: [],
                        prices: [],
                        specialties: [],
                        certificates: [],
                        cities: [],
                        organizers: [],
                    },
                });
            })
            .catch((error) => {
                if (error?.name === "AbortError") {
                    return;
                }

                setData((current) => ({
                    ...current,
                    items: [],
                    pinnedItems: [],
                    pagination: {
                        page: 1,
                        perPage: PER_PAGE,
                        totalItems: 0,
                        totalPages: 0,
                        hasMore: false,
                    },
                }));
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [endpoint, history, location.search]);

    const applyRouteState = (nextState) => {
        const search = buildRouteQuery(nextState);

        history.push({
            pathname: pagePath,
            search: search ? `?${search}` : "",
        });
    };

    const updateFilterField = (field, value) => {
        setDraft((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const resetFilterValues = (state) => ({
        ...state,
        type: "0",
        hasPrice: "0",
        speciality: "0",
        certificate: "0",
        city: "0",
        organizer: "0",
        dateFrom: "",
        dateTo: "",
        page: 1,
    });

    const handleSearchSubmit = (event) => {
        event.preventDefault();

        trackEvent("conference_search_submit", {
            content_group: "conferences",
            content_type: favoriteOnly ? "conference_list_favorite" : "conference_list_all",
        });

        applyRouteState({
            ...draft,
            page: 1,
        });
    };

    const handleSortChange = (event) => {
        const nextState = {
            ...draft,
            sort: normalizeSortValue(event.target.value),
            page: 1,
        };

        setDraft(nextState);
        trackEvent("conference_sort_change", {
            content_group: "conferences",
            content_type: favoriteOnly ? "conference_list_favorite" : "conference_list_all",
            sort: nextState.sort,
        });
        applyRouteState(nextState);
    };

    const applyFilters = () => {
        trackEvent("conference_filters_apply", {
            content_group: "conferences",
            content_type: favoriteOnly ? "conference_list_favorite" : "conference_list_all",
        });

        applyRouteState({
            ...draft,
            page: 1,
        });
        setIsFilterOpen(false);
    };

    const resetFilters = () => {
        const nextState = resetFilterValues({
            ...draft,
        });

        setDraft(nextState);
        trackEvent("conference_filters_reset", {
            content_group: "conferences",
            content_type: favoriteOnly ? "conference_list_favorite" : "conference_list_all",
        });
        applyRouteState(nextState);
    };

    const clearActiveFilters = () => {
        const nextState = resetFilterValues({
            ...queryState,
        });

        setDraft(nextState);
        applyRouteState(nextState);
    };

    const removeValueFilter = (field) => {
        const nextState = {
            ...queryState,
            [field]: "0",
            page: 1,
        };

        setDraft((current) => ({
            ...current,
            [field]: "0",
            page: 1,
        }));
        applyRouteState(nextState);
    };

    const removeDateFilter = () => {
        const nextState = {
            ...queryState,
            dateFrom: "",
            dateTo: "",
            page: 1,
        };

        setDraft((current) => ({
            ...current,
            dateFrom: "",
            dateTo: "",
            page: 1,
        }));
        applyRouteState(nextState);
    };

    const handlePageClick = (nextPage) => {
        if (nextPage < 1 || nextPage > (data.pagination.totalPages || 1) || nextPage === (data.pagination.page || 1)) {
            return;
        }

        trackEvent("conference_pagination_click", {
            content_group: "conferences",
            content_type: favoriteOnly ? "conference_list_favorite" : "conference_list_all",
            page: nextPage,
        });

        applyRouteState({
            ...queryState,
            page: nextPage,
        });
        window.scrollTo({top: 0, behavior: "smooth"});
    };

    const renderActiveChip = (field, label) => {
        if (!label || queryState[field] === "0") {
            return null;
        }

        return (
            <button
                className="conf-chip is-active"
                type="button"
                onClick={() => removeValueFilter(field)}>
                {label}
            </button>
        );
    };

    const renderFilterSelect = (field, value, options, resolveLabel = (item) => item.label) => (
        <select
            className="conf-select"
            value={value}
            onChange={(event) => updateFilterField(field, event.target.value)}>
            <option value="0">{t("conferences.filters.all")}</option>
            {(options || []).map((item) => (
                <option key={`${field}-${item.value}`} value={String(item.value)}>
                    {resolveLabel(item)}
                </option>
            ))}
        </select>
    );

    let emptyMessage = t("conferences.browser.noResults", "No events found.");
    if (favoriteOnly) {
        emptyMessage = hasSearchTerm || hasActiveFilterChips
            ? t("conferences.browser.noFavoritesFor", "No favorite events match your filters.")
            : t("conferences.browser.noFavorites", "You do not have favorite events yet.");
    } else if (hasSearchTerm || hasActiveFilterChips) {
        emptyMessage = t("conferences.browser.noResultsFor", "No events match your filters.");
    }

    return (
        <section className="conf-browser">
            <div className="container">
                <div className="conf-browser__head">
                    <div className="conf-browser__title">
                        <h1>{t("conferences.header")}</h1>
                        <div className="conf-browser__meta">
                            <span className="conf-muted">{t("conferences.browser.found", "Found")}: </span>
                            <strong>{data.pagination.totalItems || 0}</strong>
                        </div>
                    </div>

                    {!!auth.user && (
                        <nav className="conf-browser__tabs" aria-label={t("conferences.browser.tabsLabel", "Lists")}>
                            <NavLink
                                className="conf-browser__tab"
                                exact
                                activeClassName="active"
                                to={{
                                    pathname: "/conferences",
                                    search: preservedSearch ? `?${preservedSearch}` : "",
                                }}>
                                <span>{t("conferences.tabs.all")}</span>
                            </NavLink>
                            <NavLink
                                className="conf-browser__tab"
                                exact
                                activeClassName="active"
                                to={{
                                    pathname: "/conferences/favorite",
                                    search: preservedSearch ? `?${preservedSearch}` : "",
                                }}>
                                <span>{t("conferences.tabs.favorite")}</span>
                            </NavLink>
                        </nav>
                    )}

                    <div className="conf-browser__actions">
                        <button
                            className="conf-btn conf-btn--ghost conf-browser__filters-btn"
                            type="button"
                            onClick={() => setIsFilterOpen(true)}>
                            {t("search.filters.title", "Filters")}
                        </button>

                        <label className="conf-bar__sort" htmlFor={`conference-sort-${favoriteOnly ? "favorites" : "all"}`}>
                            <span>{t("search.filters.sort", "Sort")}</span>
                            <select
                                id={`conference-sort-${favoriteOnly ? "favorites" : "all"}`}
                                value={draft.sort}
                                onChange={handleSortChange}>
                                <option value="newest">{t("conferences.browser.sort.newest", "Newest")}</option>
                                <option value="upcoming">{t("conferences.browser.sort.upcoming", "Upcoming")}</option>
                            </select>
                        </label>
                    </div>
                </div>

                <AdvertiseBlock
                    unitId="conferences1"
                    blockLocation="top1"
                />

                <div className="conf-browser__shell">
                    <header className="conf-bar">
                        <form className="conf-bar__form" onSubmit={handleSearchSubmit} role="search">
                            <div className="conf-bar__search">
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M10.5 3a7.5 7.5 0 105.02 13.08l3.2 3.2a1 1 0 001.41-1.41l-3.2-3.2A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z" fill="currentColor" />
                                </svg>
                                <input
                                    type="search"
                                    value={draft.search}
                                    onChange={(event) => updateFilterField("search", event.target.value)}
                                    placeholder={t("conferences.browser.searchPlaceholder", "Search events")}
                                />
                            </div>

                            <button className="conf-btn conf-btn--primary conf-bar__submit" type="submit">
                                {t("search.submit", "Search")}
                            </button>
                        </form>
                    </header>

                    <div className="conf-context">
                        <div className="conf-context__left">
                            <span className="conf-muted">{t("conferences.browser.stateLabel", "State")}:</span>
                            <strong className="conf-context__state">{stateLabel}</strong>
                        </div>

                        <div className="conf-context__right">
                            {!hasActiveFilterChips && (
                                <span className="conf-chip is-active is-static">
                                    {t("conferences.filters.all")}
                                </span>
                            )}

                            {renderActiveChip("type", findFilterLabel(data.filtersMeta.types, queryState.type, t))}
                            {renderActiveChip("hasPrice", findFilterLabel(data.filtersMeta.prices, queryState.hasPrice, t))}
                            {renderActiveChip("speciality", findFilterLabel(data.filtersMeta.specialties, queryState.speciality, t))}
                            {renderActiveChip("certificate", findFilterLabel(data.filtersMeta.certificates, queryState.certificate, t))}
                            {renderActiveChip("city", findFilterLabel(data.filtersMeta.cities, queryState.city, t))}
                            {renderActiveChip("organizer", findFilterLabel(data.filtersMeta.organizers, queryState.organizer, t))}
                            {(queryState.dateFrom || queryState.dateTo) && (
                                <button className="conf-chip is-active" type="button" onClick={removeDateFilter}>
                                    {[queryState.dateFrom, queryState.dateTo].filter(Boolean).join(" — ")}
                                </button>
                            )}

                            {hasActiveFilterChips && (
                                <button className="conf-chip" type="button" onClick={clearActiveFilters}>
                                    {t("conferences.browser.clear", "Clear filters")}
                                </button>
                            )}
                        </div>
                    </div>

                    {isLoading && (
                        <div className="conf-browser__status">
                            {t("conferences.browser.loading", "Loading events...")}
                        </div>
                    )}

                    {!isLoading && sortedPinnedItems.length === 0 && conferenceItems.length === 0 && (
                        <div className="conf-browser__empty">{emptyMessage}</div>
                    )}

                    {(sortedPinnedItems.length > 0 || conferenceItems.length > 0) && (
                        <div className="conf-grid conf-browser__results">
                            {sortedPinnedItems.map((entry) => {
                                if (!entry?.item) {
                                    return null;
                                }

                                return (
                                    <ConferenceCard
                                        key={`pinned-conference-${entry.item.id}`}
                                        item={entry.item}
                                        isPinned
                                        t={t}
                                    />
                                );
                            })}

                            {conferenceItems.map((item) => (
                                <ConferenceCard
                                    key={`conference-${item.id}`}
                                    item={item}
                                    t={t}
                                />
                            ))}
                        </div>
                    )}

                    {(data.pagination.totalPages || 0) > 1 && (
                        <nav className="conf-pagination" aria-label={t("conferences.browser.paginationLabel", "Pagination")}>
                            <button
                                className="conf-pagination__button"
                                type="button"
                                onClick={() => handlePageClick((data.pagination.page || 1) - 1)}
                                disabled={(data.pagination.page || 1) <= 1}>
                                {t("search.prev", "Previous")}
                            </button>

                            {paginationItems.map((item) => {
                                if (typeof item !== "number") {
                                    return (
                                        <span className="conf-pagination__ellipsis" key={item}>
                                            …
                                        </span>
                                    );
                                }

                                return (
                                    <button
                                        className={`conf-pagination__button ${(data.pagination.page || 1) === item ? "is-current" : ""}`}
                                        key={`page-${item}`}
                                        type="button"
                                        onClick={() => handlePageClick(item)}>
                                        {item}
                                    </button>
                                );
                            })}

                            <button
                                className="conf-pagination__button"
                                type="button"
                                onClick={() => handlePageClick((data.pagination.page || 1) + 1)}
                                disabled={(data.pagination.page || 1) >= (data.pagination.totalPages || 1)}>
                                {t("search.next", "Next")}
                            </button>
                        </nav>
                    )}
                </div>
            </div>

            <div className={`conf-filters-backdrop ${isFilterOpen ? "is-open" : ""}`} onClick={() => setIsFilterOpen(false)} />

            <aside
                className={`conf-filters ${isFilterOpen ? "is-open" : ""}`}
                hidden={!isFilterOpen}
                aria-label={t("search.filters.title", "Filters")}>
                <div className="conf-filters__head">
                    <strong>{t("search.filters.title", "Filters")}</strong>
                    <button
                        className="conf-iconbtn"
                        type="button"
                        onClick={() => setIsFilterOpen(false)}
                        aria-label={t("conferences.browser.close", "Close")}>
                        <span>✕</span>
                    </button>
                </div>

                <div className="conf-filters__body">
                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.type")}</div>
                        {renderFilterSelect("type", draft.type, data.filtersMeta.types)}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.price.caption")}</div>
                        {renderFilterSelect("hasPrice", draft.hasPrice, data.filtersMeta.prices, (item) => (
                            item.labelKey ? t(item.labelKey) : item.label
                        ))}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.specialty")}</div>
                        {renderFilterSelect("speciality", draft.speciality, data.filtersMeta.specialties)}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.certification")}</div>
                        {renderFilterSelect("certificate", draft.certificate, data.filtersMeta.certificates)}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.city")}</div>
                        {renderFilterSelect("city", draft.city, data.filtersMeta.cities)}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.filters.organizer")}</div>
                        {renderFilterSelect("organizer", draft.organizer, data.filtersMeta.organizers)}
                    </div>

                    <div className="conf-group">
                        <div className="conf-group__title">{t("conferences.browser.date", "Date")}</div>
                        <div className="conf-range">
                            <input
                                type="date"
                                value={draft.dateFrom}
                                onChange={(event) => updateFilterField("dateFrom", event.target.value)}
                            />
                            <input
                                type="date"
                                value={draft.dateTo}
                                onChange={(event) => updateFilterField("dateTo", event.target.value)}
                            />
                        </div>
                    </div>

                    <div className="conf-filters__actions">
                        <button className="conf-btn conf-btn--primary conf-btn--full" type="button" onClick={applyFilters}>
                            {t("conferences.filters.apply")}
                        </button>
                        <button className="conf-btn conf-btn--ghost conf-btn--full" type="button" onClick={resetFilters}>
                            {t("conferences.filters.clear")}
                        </button>
                    </div>
                </div>
            </aside>
        </section>
    );
}

export default ConferenceBrowser;
